import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Bell, Download, History, MapPinned, Radio, Receipt, ShieldAlert, TicketPercent, Wallet } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { money, when } from "@/lib/rides";
import { PageHead } from "@/pages/admin/AdminLayout";
import { RideAdminCard } from "@/pages/admin/RideAdminCard";
import { StatusBadge } from "@/components/RideStatus";
import { AnnouncementStrip } from "@/components/Announcement";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const ymd = (d) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);

export function AdminAnnouncements() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["adminAnnouncement"], queryFn: () => api.get("/admin/announcement").then((r) => r.data) });
  const [text, setText] = useState(null);
  const value = text ?? data?.text ?? "";
  const save = async (published) => {
    try {
      await api.put("/admin/announcement", { text: value, published });
      toast.success(published ? "Announcement published" : data?.published ? "Announcement unpublished" : "Draft saved");
      ["adminAnnouncement", "announcement"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
      setText(null);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  return (
    <div className="fade-up max-w-2xl space-y-5" data-testid="admin-announcements">
      <PageHead title="Announcements" sub="One line shown across every Rider and Driver screen while published." />
      <div className="space-y-3 rounded-xl border bg-card p-4">
        <p className="text-sm">Status: <b data-testid="announcement-status" className={data?.published ? "text-primary" : "text-muted-foreground"}>{data?.published ? "PUBLISHED" : "NOT PUBLISHED"}</b>
          {data?.updated_by && <span className="text-xs text-muted-foreground"> · last saved by {data.updated_by} {when(data.updated_at)}</span>}</p>
        <Input maxLength={200} placeholder="e.g. Game day Saturday! Book early for rides to Bobcat Stadium." value={value} onChange={(e) => setText(e.target.value)} data-testid="announcement-text-input" />
        <p className="text-[11px] text-muted-foreground">{value.length}/200 characters · single line</p>
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Preview</p>
        {value.trim() ? <AnnouncementStrip text={value.trim()} fixed={false} testid="announcement-preview" /> : <p className="text-xs text-muted-foreground">Type text to preview.</p>}
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => save(!!data?.published)} disabled={!!data?.published && !value.trim()} data-testid="announcement-save-button">Save changes</Button>
          {data?.published
            ? <Button variant="destructive" onClick={() => save(false)} data-testid="announcement-unpublish-button">Unpublish</Button>
            : <Button onClick={() => save(true)} disabled={!value.trim()} data-testid="announcement-publish-button">Publish</Button>}
        </div>
      </div>
    </div>
  );
}

async function download(path, params, name) {
  try {
    const res = await api.get(path, { params, responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = Object.assign(document.createElement("a"), { href: url, download: name });
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Export downloaded");
  } catch (e) {
    toast.error(formatApiError(e));
  }
}

function Retention() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["adminRetention"], queryFn: () => api.get("/admin/retention").then((r) => r.data) });
  if (!data) return null;
  const expired = data.messages.expired + data.trips.expired;
  const purge = async () => {
    if (!window.confirm(`Permanently delete ${data.trips.expired} trip records and ${data.messages.expired} messages older than ${data.days} days? Export them first.`)) return;
    try {
      const { data: r } = await api.post("/admin/retention/purge", { confirm: true });
      toast.success(`Purged ${r.rides_deleted} trips and ${r.messages_deleted} messages`);
      qc.invalidateQueries({ queryKey: ["adminRetention"] });
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  return (
    <section className="space-y-3 rounded-xl border bg-card p-4" data-testid="retention-panel">
      <h2 className="flex items-center gap-2 font-display text-lg font-semibold"><ShieldAlert className="h-4 w-4 text-primary" />{data.days}-day retention</h2>
      <p className="text-xs text-muted-foreground">Messages and ended trip records are kept {data.days} days. Nothing is deleted until you purge. Upcoming rides, recurring schedules, accounts, unpaid balances and the credit ledger are never purged.</p>
      <div className="grid gap-2 text-sm sm:grid-cols-2">
        <p data-testid="retention-messages">Messages: {data.messages.total} · <b className="text-amber-300">{data.messages.expiring_soon} expiring within {data.warn_days} days</b> · {data.messages.expired} past {data.days} days</p>
        <p data-testid="retention-trips">Trips: {data.trips.total} · <b className="text-amber-300">{data.trips.expiring_soon} expiring within {data.warn_days} days</b> · {data.trips.expired} past {data.days} days</p>
      </div>
      <p className="text-xs" data-testid="retention-last-export">Last export: {data.last_export ? `${data.last_export.kind} by ${data.last_export.by}, ${when(data.last_export.at)}` : "never"}</p>
      {data.expiring_trips.map((t) => (
        <p key={t.id} className="text-xs text-muted-foreground" data-testid={`expiring-trip-${t.id}`}>{t.route} · {t.rider_name} · {t.status} · expires {when(t.expires_at)}</p>
      ))}
      <Button variant="destructive" size="sm" disabled={!expired} onClick={purge} data-testid="retention-purge-button">Purge {expired} expired records</Button>
    </section>
  );
}

export function AdminReports() {
  const now = new Date();
  const [range, setRange] = useState({ start: ymd(new Date(now.getFullYear(), now.getMonth(), 1)), end: ymd(now) });
  const [month, setMonth] = useState(ymd(now).slice(0, 7));
  return (
    <div className="fade-up max-w-3xl space-y-5" data-testid="admin-reports">
      <PageHead title="Reports / Exports" sub="Download trip and message records as CSV before they reach the retention limit." />
      <section className="space-y-3 rounded-xl border bg-card p-4">
        <h2 className="font-display text-lg font-semibold">By date range</h2>
        <div className="flex flex-wrap gap-2">
          <Input type="date" value={range.start} onChange={(e) => setRange({ ...range, start: e.target.value })} className="w-44" data-testid="export-start-input" />
          <Input type="date" value={range.end} onChange={(e) => setRange({ ...range, end: e.target.value })} className="w-44" data-testid="export-end-input" />
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => download("/admin/exports/trips", range, `scoopz-trips-${range.start}-to-${range.end}.csv`)} data-testid="export-trips-button"><Download className="mr-1 h-4 w-4" />Trips CSV</Button>
          <Button variant="outline" onClick={() => download("/admin/exports/messages", range, `scoopz-messages-${range.start}-to-${range.end}.csv`)} data-testid="export-messages-button"><Download className="mr-1 h-4 w-4" />Messages CSV</Button>
        </div>
      </section>
      <section className="space-y-3 rounded-xl border bg-card p-4">
        <h2 className="font-display text-lg font-semibold">Monthly offload</h2>
        <div className="flex flex-wrap gap-2">
          <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-44" data-testid="export-month-input" />
          <Button onClick={() => download("/admin/exports/monthly", { month }, `scoopz-monthly-${month}.csv`)} data-testid="export-monthly-button"><Download className="mr-1 h-4 w-4" />Monthly CSV (trips + messages)</Button>
        </div>
      </section>
      <Retention />
    </div>
  );
}

export function AdminAssignments() {
  const { data } = useQuery({ queryKey: ["adminUnassigned"], queryFn: () => api.get("/admin/rides", { params: { view: "unassigned" } }).then((r) => r.data), refetchInterval: 10000 });
  return (
    <div className="fade-up space-y-4" data-testid="admin-assignments">
      <PageHead title="Ride Assignments" sub={`${data?.length ?? 0} upcoming rides without a driver. Recurring and split rides are offered to drivers as whole ride sets.`} />
      {!data?.length && <p className="rounded-xl border border-dashed p-4 text-sm text-muted-foreground">Every upcoming ride has a driver.</p>}
      <div className="grid gap-3 lg:grid-cols-2">{data?.map((r) => <RideAdminCard key={r.id} ride={r} />)}</div>
    </div>
  );
}

export function AdminPastRides() {
  const qc = useQueryClient();
  const [status, setStatus] = useState("");
  const { data } = useQuery({ queryKey: ["adminPast", status], queryFn: () => api.get("/admin/rides/historical", { params: { status: status || undefined } }).then((r) => r.data) });
  const voidRide = async (id) => {
    try {
      await api.post(`/admin/rides/${id}/historical/void`);
      toast.success("Past ride voided");
      qc.invalidateQueries({ queryKey: ["adminPast"] });
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  return (
    <div className="fade-up space-y-4" data-testid="admin-past-rides">
      <PageHead title="Past rides" sub="Rides recorded by admin up to 10 days back. Each needs the rider's approval before it counts.">
        <Button asChild variant="outline"><Link to="/admin/book?mode=past" data-testid="record-past-ride-link">Record a past ride</Link></Button>
      </PageHead>
      <div className="flex gap-2">
        {[["", "All"], ["pending", "Pending rider approval"], ["approved", "Approved"], ["disputed", "Disputed"]].map(([k, l]) => (
          <button key={k} type="button" onClick={() => setStatus(k)} data-testid={`past-filter-${k || "all"}`}
            className={`rounded-full border px-3 py-1 text-xs font-semibold ${status === k ? "border-primary bg-primary text-primary-foreground" : "border-white/15"}`}>{l}</button>
        ))}
      </div>
      <div className="divide-y rounded-xl border bg-card">
        {!data?.length && <p className="p-4 text-sm text-muted-foreground">No past rides.</p>}
        {data?.map((r) => (
          <div key={r.id} className="flex flex-wrap items-center gap-3 p-3 text-sm" data-testid={`past-ride-row-${r.id}`}>
            <span className="min-w-0 flex-1"><span className="block font-semibold">{r.rider_name} · {r.pickup.name} → {r.dropoff.name}</span>
              <span className="text-xs text-muted-foreground">{when(r.scheduled_at)} · {money(r.fare)}{r.historical_review?.reason ? ` · Dispute: ${r.historical_review.reason}` : ""}</span></span>
            <StatusBadge status={r.status} />
            {["pending_rider_approval", "disputed"].includes(r.status) && <Button size="sm" variant="ghost" onClick={() => voidRide(r.id)} data-testid={`past-ride-void-${r.id}`}>Void</Button>}
          </div>
        ))}
      </div>
    </div>
  );
}

const HUB = [
  { to: "/admin/live", label: "Live rides", icon: Radio }, { to: "/admin/notifications", label: "Notifications", icon: Bell },
  { to: "/admin/directory", label: "Directory", icon: MapPinned }, { to: "/admin/discounts", label: "Discount codes", icon: TicketPercent },
  { to: "/admin/payments", label: "Payments", icon: Receipt }, { to: "/admin/credit", label: "Ride Credit", icon: Wallet },
  { to: "/admin/past-rides", label: "Past rides (rider approval)", icon: History }, { to: "/admin/audit", label: "Audit history", icon: ShieldAlert },
];

export function AdminSettings() {
  return (
    <div className="fade-up space-y-4" data-testid="admin-settings">
      <PageHead title="Settings" sub="Directory, payments, credit, codes and the audit trail." />
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {HUB.map((h) => (
          <Link key={h.to} to={h.to} className="flex items-center gap-3 rounded-xl border bg-card p-4 font-semibold transition-colors hover:border-primary/50" data-testid={`settings-link-${h.to.split("/").pop()}`}>
            <h.icon className="h-4 w-4 text-primary" />{h.label}
          </Link>
        ))}
      </div>
    </div>
  );
}
